import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Logo } from './Logo';
const STORAGE_KEY = 'riam_intro_shown';
export function IntroCurtain() {
  const [visible, setVisible] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      const alreadyShown = sessionStorage.getItem(STORAGE_KEY);
      const prefersReduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      return !alreadyShown && !prefersReduce;
    } catch {
      return true;
    }
  });
  const [phase, setPhase] = useState<'logo' | 'opening' | 'done'>('logo');
  useEffect(() => {
    if (!visible) return;
    document.body.style.overflow = 'hidden';
    try {
      sessionStorage.setItem(STORAGE_KEY, '1');
    } catch {}
    // Preload critical images so they're ready when the curtain opens
    const preloadLinks = ['https://cdn.magicpatterns.com/uploads/ak85GV4ZeQoCc5YgRxs6SS/BANNER_RIAM.png', 'https://cdn.magicpatterns.com/uploads/3LsFraVfZ6SGDyzpZYZmJe/RIAM_LOGO.png'].map(href => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = href;
      document.head.appendChild(link);
      return link;
    });
    // Hold the logo long enough to read the tagline, then trigger opening
    const openTimer = setTimeout(() => setPhase('opening'), 3000);
    // Hard safety fallback: even if animation events fail (e.g. throttled mobile browser),
    // force the curtain to unmount after 10 seconds total. User is never stuck.
    const safetyTimer = setTimeout(() => {
      setPhase('done');
      setVisible(false);
    }, 10000);
    return () => {
      clearTimeout(openTimer);
      clearTimeout(safetyTimer);
      document.body.style.overflow = '';
      preloadLinks.forEach(l => l.parentNode?.removeChild(l));
    };
  }, [visible]);
  // Called when the LEFT curtain finishes its slide-out animation.
  // This is the authoritative "animation done" signal — far more reliable than a setTimeout.
  const handleCurtainOpenComplete = () => {
    if (phase === 'opening') {
      setPhase('done');
      setVisible(false);
    }
  };
  return <AnimatePresence data-id="element-335">
      {visible && <div className="fixed inset-0 z-[100] pointer-events-none" aria-hidden="true" data-id="element-336">
          {/* Left curtain panel — owns the "animation complete" callback */}
          <motion.div className="absolute top-0 left-0 h-full w-1/2 bg-riam-black" initial={{
        x: 0
      }} animate={{
        x: phase === 'opening' ? '-101%' : 0
      }} transition={{
        duration: 2.0,
        ease: 'easeInOut'
      }} onAnimationComplete={handleCurtainOpenComplete} style={{
        willChange: 'transform'
      }} data-id="element-337">
            <div className="absolute top-0 right-0 h-full w-px" style={{
          background: 'linear-gradient(180deg, transparent 0%, #8a6d3b 15%, #f0d894 50%, #8a6d3b 85%, transparent 100%)',
          boxShadow: '0 0 18px rgba(232, 201, 122, 0.5)'
        }} data-id="element-338" />
          </motion.div>

          {/* Right curtain panel */}
          <motion.div className="absolute top-0 right-0 h-full w-1/2 bg-riam-black" initial={{
        x: 0
      }} animate={{
        x: phase === 'opening' ? '101%' : 0
      }} transition={{
        duration: 2.0,
        ease: 'easeInOut'
      }} style={{
        willChange: 'transform'
      }} data-id="element-339">
            <div className="absolute top-0 left-0 h-full w-px" style={{
          background: 'linear-gradient(180deg, transparent 0%, #8a6d3b 15%, #f0d894 50%, #8a6d3b 85%, transparent 100%)',
          boxShadow: '0 0 18px rgba(232, 201, 122, 0.5)'
        }} data-id="element-340" />
          </motion.div>

          {/* Centered logo + ornament */}
          <div className="absolute inset-0 flex flex-col items-center justify-center px-6" data-id="element-341">
            <motion.div initial={{
          opacity: 0,
          scale: 0.9,
          y: 10
        }} animate={{
          opacity: phase === 'opening' ? 0 : 1,
          scale: phase === 'opening' ? 0.92 : 1,
          y: 0
        }} transition={{
          opacity: {
            duration: phase === 'opening' ? 0.5 : 0.8,
            ease: 'easeOut'
          },
          scale: {
            duration: phase === 'opening' ? 0.6 : 1.0,
            ease: 'easeOut'
          },
          y: {
            duration: 0.8,
            ease: 'easeOut'
          }
        }} className="flex flex-col items-center" data-id="element-342">
              <Logo size="lg" data-id="element-343" />

              <motion.div initial={{
            scaleX: 0,
            opacity: 0
          }} animate={{
            scaleX: 1,
            opacity: 1
          }} transition={{
            duration: 0.9,
            delay: 0.3,
            ease: 'easeOut'
          }} className="gold-divider w-40 mt-6 origin-center" data-id="element-344" />

              <motion.p initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: phase === 'opening' ? 0 : 1,
            y: 0
          }} transition={{
            duration: 0.7,
            delay: 0.5
          }} className="gold-shine-soft font-sans text-xs md:text-sm tracking-[0.35em] uppercase mt-6 text-center" data-id="element-345">
                Tu transformación empieza aquí
              </motion.p>
            </motion.div>
          </div>
        </div>}
    </AnimatePresence>;
}